import { Col, Input, message, Modal, notification, Radio, Row, Select, Space, Upload } from 'antd'
import { Button, Typography } from 'modules/core/components'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { UploadOutlined } from '@ant-design/icons'
import { connect } from 'react-redux'
import planning from 'modules/planning'
import { useParams } from 'react-router-dom'
import { generateMessageError, generateSelectOptions, sendUserDataToGTM } from 'modules/core/utils'
import { CONCEPTS } from 'modules/core/constants'
import configuration from 'modules/configuration'
import moment from 'moment'
import login from 'modules/login'

const CREATE_OPTIONS = [
  {
    id: 'file',
    name: 'ACTION_UPLOAD_FILE',
  },
  {
    id: 'manual',
    name: 'ACTION_MANUAL_CREATE',
  },
]

const ADJUSTMENTS_TYPE = [
  {
    id: 'EXPANSION',
    name: 'LABEL_EXPANSION',
  },
  {
    id: 'REDUCTION',
    name: 'LABEL_REDUCTION',
  },
  {
    id: 'RECLASSIFICATION',
    name: 'LABEL_RECLASSIFICATION',
  },
]

const CreateApplicationModal = ({
  visible,
  onClose,
  onRedirect,
  onRefetch,
  downloadTemplateAdjustment,
  fetchCurrencyList,
  currencyList,
  uploadAdjustmentXLS,
  fetchAreasList,
  areaList,
  fetchAllDimensions,
  fetchAllDimensionsValues,
  allDimensionsList,
  fetchBudgetTypeList,
  budgetTypeList,
  loggedUser,
}) => {
  const [dataType, setDataType] = useState('file')
  const [fileList, setFileList] = useState([])
  const [adjustmentType, setAdjustmentType] = useState(null)
  const [currency, setCurrency] = useState(null)
  const [area, setArea] = useState(null)
  const [society, setSociety] = useState(null)
  const [societyOptions, setSocietyOptions] = useState([])
  const [concept, setConcept] = useState(null)
  const [description, setDescription] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const { t } = useTranslation()
  let { periodId, globalBudgetId } = useParams()

  const societyDimension = allDimensionsList.find((dimension) => {
    const dimensionName = dimension?.name_destination?.toLowerCase()
    return dimensionName === 'sociedad' || dimensionName === 'society'
  })

  useEffect(() => {
    fetchCurrencyList()
  }, [fetchCurrencyList])

  useEffect(() => {
    if (currency === null && currencyList?.data?.length > 0) {
      const colCurrency = currencyList.data.find((c) => c.code === 'COP')
      if (colCurrency) setCurrency(colCurrency.id)
    }
  }, [currencyList, currency])

  useEffect(() => {
    if (concept === null && budgetTypeList?.values?.length > 0) {
      const expensesConcept = budgetTypeList.values.find((c) => c.id === CONCEPTS.IDS.EXPENSES_ID)
      if (expensesConcept) setConcept(expensesConcept.id)
    }
  }, [budgetTypeList, concept])

  useEffect(() => {
    if (visible) {
      fetchAreasList()
      fetchAllDimensions()
      fetchBudgetTypeList()
    }
  }, [fetchAreasList, fetchAllDimensions, fetchBudgetTypeList, visible])

  useEffect(() => {
    if (visible && societyDimension?.id) {
      fetchAllDimensionsValues({ id_dimension: societyDimension.id }).then((response) => {
        setSocietyOptions(
          response.payload.data
            .filter(
              (dimensionValue) =>
                !dimensionValue.name_destination?.toLowerCase().includes('valor sin asignar'),
            )
            .map((dimensionValue) => {
              return {
                ...dimensionValue,
                name: dimensionValue.name_destination,
                code: dimensionValue.code_destination,
              }
            }),
        )
      })
    }
  }, [fetchAllDimensionsValues, societyDimension, visible])

  const onAddUploadFile = (file) => {
    setFileList([file])
    return false
  }

  const uploadProps = {
    name: 'file',
    accept: '.xls, .xlsx',
    showUploadList: { showPreviewIcon: false },
    beforeUpload: onAddUploadFile,
    onRemove: (file) => setFileList([]),
    fileList: fileList,
    disabled: !adjustmentType || !currency || !area || !society || !concept,
  }

  const resetFields = () => {
    setCurrency(null)
    setAdjustmentType(null)
    setFileList([])
    setDataType('file')
    setArea(null)
    setSociety(null)
    setSocietyOptions([])
    setConcept(null)
    setDescription('')
  }

  const onCancel = () => {
    onClose()
    resetFields()
  }

  const onConfirm = () => {
    if (dataType === 'file') {
      setIsSaving(true)
      uploadAdjustmentXLS(fileList, {
        period_id: periodId,
        currency,
        area,
        society,
        adjustment_type: adjustmentType,
        concept_id: concept,
        module: 'OVERALL_PLANNING',
        projection: globalBudgetId,
        description,
      })
        .then(() => {
          onRefetch()
          setIsSaving(false)
          resetFields()
          onClose()
          sendUserDataToGTM(loggedUser, 'SAC', `Crear SAC`, `Creación`)
          message.success(t('FEEDBACK_CHANGES_SAVED_SUCCESS'), 8)
        })
        .catch((error) => {
          setIsSaving(false)
          resetFields()
          onClose()
          notification.error({
            message: t('FEEDBACK_DEFAULT_ERROR'),
            description: generateMessageError(error),
            duration: 0,
          })
        })
    } else {
      onRedirect()
      setDataType('file')
      onClose()
    }
  }

  return (
    <Modal
      title={t('PLANNING_SAC_CREATE_NEW_ACTION_BUTTON_NAME')}
      visible={visible}
      onCancel={onCancel}
      okText={dataType === 'file' ? t('ACTION_UPLOAD') : t('ACTION_GO')}
      cancelText={t('ACTION_CANCEL')}
      onOk={onConfirm}
      centered
      forceRender
      destroyOnClose
      okButtonProps={{ loading: isSaving }}
      cancelButtonProps={{ disabled: isSaving }}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <Radio.Group
          defaultValue={dataType}
          key={dataType}
          style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
        >
          {CREATE_OPTIONS.map((option) => (
            <Radio
              value={option.id}
              key={option.id}
              onChange={(val) => setDataType(val.target.value)}
            >
              {t(option.name)}
            </Radio>
          ))}
        </Radio.Group>
        {dataType === 'file' && (
          <Row gutter={[24, 24]} type="flex" justify="center">
            <Col span={24} style={{ display: 'flex', justifyContent: 'center' }}>
              <Space direction="vertical">
                <Space direction="horizontal">
                  <div style={{ width: 110, textAlign: 'right' }}>
                    <Typography.Body level={2}>{`${t('ADJUSTMENT_TYPE_FIELD')}:`}</Typography.Body>
                  </div>
                  <Select
                    placeholder={t('ACTION_SELECT')}
                    style={{ width: 220 }}
                    bordered={false}
                    key={adjustmentType}
                    defaultValue={adjustmentType}
                    onChange={(val) => setAdjustmentType(val)}
                    options={generateSelectOptions({
                      options: ADJUSTMENTS_TYPE.map((el) => {
                        return { ...el, name: t(el.name) }
                      }),
                    })}
                  />
                </Space>
                <Space>
                  <div style={{ width: 110, textAlign: 'right' }}>
                    <Typography.Body level={2}>{`${t('LABEL_CURRENCY')}:`}</Typography.Body>
                  </div>
                  <Select
                    placeholder={t('ACTION_SELECT')}
                    style={{ width: 220 }}
                    bordered={false}
                    value={currency ?? undefined}
                    disabled
                    onChange={(val) => setCurrency(val)}
                    options={generateSelectOptions({ options: currencyList.data })}
                  />
                </Space>
                <Space>
                  <div style={{ width: 110, textAlign: 'right' }}>
                    <Typography.Body level={2}>{`${t('FIELD_CONCEPT')}:`}</Typography.Body>
                  </div>
                  <Select
                    placeholder={t('ACTION_SELECT')}
                    style={{ width: 220 }}
                    bordered={false}
                    value={concept ?? undefined}
                    disabled
                    onChange={(val) => setConcept(val)}
                    options={generateSelectOptions({ options: budgetTypeList?.values })}
                  />
                </Space>
                <Space>
                  <div style={{ width: 110, textAlign: 'right' }}>
                    <Typography.Body level={2}>{`${t('LABEL_COMPANY')}:`}</Typography.Body>
                  </div>
                  <Select
                    placeholder={t('ACTION_SELECT')}
                    style={{ width: 220 }}
                    bordered={false}
                    key={society}
                    defaultValue={society}
                    onChange={(val) => setSociety(val)}
                    options={generateSelectOptions({ options: societyOptions })}
                  />
                </Space>
                <Space>
                  <div style={{ width: 110, textAlign: 'right' }}>
                    <Typography.Body level={2}>{`${t('LABEL_AREA')}:`}</Typography.Body>
                  </div>
                  <Select
                    placeholder={t('ACTION_SELECT')}
                    style={{ width: 220 }}
                    bordered={false}
                    key={area}
                    defaultValue={area}
                    onChange={(val) => setArea(val)}
                    options={generateSelectOptions({ options: areaList })}
                  />
                </Space>
                <Space>
                  <div style={{ width: 110, textAlign: 'right' }}>
                    <Typography.Body level={2}>{`${t('FIELD_OBSERVATIONS')}:`}</Typography.Body>
                  </div>
                  <Input.TextArea
                    placeholder={t('WRITE_SOMETHING_PLACEHOLDER')}
                    autoSize={{ minRows: 1, maxRows: 3 }}
                    style={{ width: 220 }}
                    bordered={false}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </Space>
              </Space>
            </Col>
            <Col>
              <Upload {...uploadProps}>
                {uploadProps.fileList.length <= 0 && (
                  <Button.DefaultWhitILeftIcon
                    icon={<UploadOutlined />}
                    title="UPLOAD_FILE_PLACEHOLDER"
                    disabled={!adjustmentType || !currency || !area || !society || !concept}
                    tooltipTitle={
                      !adjustmentType || !currency || !area || !society || !concept
                        ? t('PLANNING_SAC_INFORMATIVE_CREATION_TOOLTIP_TITLE')
                        : ''
                    }
                  />
                )}
              </Upload>
            </Col>
            <Col>
              <div className="upload-moda-help">
                {t('INFO_UPLOAD_FILE_MODAL_DESCRIPTION')}{' '}
                <span
                  onClick={() =>
                    adjustmentType &&
                    concept &&
                    downloadTemplateAdjustment(
                      t('PLANNING_SAC_TEMPLATE_NAME', { date: moment().format('lll') }),
                      {
                        period_id: periodId,
                        adjustment_type: adjustmentType,
                        concept_id: concept,
                      },
                    )
                  }
                  style={{
                    cursor: adjustmentType && concept ? 'pointer' : 'not-allowed',
                    opacity: adjustmentType && concept ? 1 : 0.5,
                  }}
                >
                  {t('INFO_UPLOAD_FILE_DOWNLOAD_ACTION')}
                </span>
              </div>
            </Col>
          </Row>
        )}
      </Space>
    </Modal>
  )
}

const mapStateToProps = (state) => ({
  currencyList: configuration.selectors.getCurrencyList(state),
  areaList: configuration.selectors.getAreaList(state),
  allDimensionsList: configuration.selectors.getAllDimensionsList(state),
  budgetTypeList: configuration.selectors.getBudgetTypeList(state),
  loggedUser: login.selectors.getWhoAmI(state),
})

const mapDispatchToProps = {
  downloadTemplateAdjustment: planning.actions.downloadTemplateAdjustment,
  fetchCurrencyList: configuration.actions.fetchCurrencyList,
  uploadAdjustmentXLS: planning.actions.uploadAdjustmentXLS,
  fetchAreasList: configuration.actions.fetchAreasList,
  fetchAllDimensions: configuration.actions.fetchAllDimensions,
  fetchAllDimensionsValues: configuration.actions.fetchAllDimensionsValues,
  fetchBudgetTypeList: configuration.actions.fetchBudgetTypeList,
}

export default connect(mapStateToProps, mapDispatchToProps)(CreateApplicationModal)
